import { ButtonHTMLAttributes, DetailedHTMLProps, FC, forwardRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { useFormContext, Controller, FieldError } from 'react-hook-form';
import { getDateOffset } from '@utils/convertString';
import CloseIcon from '@icons/close_icon--grey.svg?component';
import StarIcon from '@icons/star_required_icon.svg?component';
import { IDatePickerProps } from './types';

export const DatePickerComponent: FC<IDatePickerProps> = ({
  name,
  placeholder,
  minDate,
  maxDate,
  stylesProp,
  buttonStyleProp,
  label,
  isClearable = false,
  required = false,
  isDisabled = false,
  noMaxDate = false,
  showRequiredIcon = false,
}) => {
  const { control, setValue } = useFormContext();

  const CustomInput = forwardRef<
    HTMLButtonElement,
    DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
      error?: FieldError;
    }
  >(({ value, onClick, error }, ref) => (
    <>
      <button
        id={name}
        type="button"
        className={`relative flex justify-between items-center w-full h-[48px] rounded-[8px] border-[1.5px] border-custom-gray pl-4 pr-[16px] hover:border-[#414042] focus:outline-none focus:ring-0 focus:border-[#414042] ${
          error && !value && '!border-error'
        } ${isDisabled && '!bg-[#4140421A]'}`}
        style={{ ...buttonStyleProp }}
        onClick={onClick}
        ref={ref}
        disabled={isDisabled}
      >
        <span className={`${!value && 'text-[16px] text-[rgba(23,30,55,0.3)]'}`}>
          {value || placeholder}
        </span>
      </button>
      {!isDisabled && value ? (
        <button
          type="button"
          className="w-[24px] h-[24px] flex-centred z-10 absolute right-[16px] top-1/2 translate-y-[-50%] opacity-30 hover:opacity-70"
          onClick={() => {
            setValue(name, null);
          }}
        >
          <CloseIcon width={12} height={12} />
        </button>
      ) : null}
    </>
  ));
  CustomInput.displayName = 'CustomInput';

  return (
    <div className="relative w-full" style={stylesProp}>
      {label && (
        <div className="label [grid-area:end]">
          <p>{label}</p>
          {showRequiredIcon && required && <StarIcon width={12} height={12} />}
        </div>
      )}
      <Controller
        control={control}
        name={name}
        rules={{ required: required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <DatePicker
              selected={value && new Date(value)}
              onChange={(date) => {
                const offset = getDateOffset(date as Date | null);
                onChange(offset?.slice(0, 10));
              }}
              minDate={minDate}
              maxDate={noMaxDate ? undefined : maxDate || new Date()}
              className={'date-picker'}
              dateFormat="dd.MM.yyyy"
              customInput={<CustomInput error={error} />}
              wrapperClassName="w-full"
              clearButtonClassName="hidden"
              onChangeRaw={(e) => e?.preventDefault()}
              scrollableYearDropdown
              yearDropdownItemNumber={30}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable={isClearable}
              disabled={isDisabled}
            />
          </>
        )}
      />
    </div>
  );
};
